/* eslint-disable no-restricted-globals */
import { pdf } from "@react-pdf/renderer";

// Lays out and writes an export PDF off the main thread, so a large export
// doesn't freeze the page.
//
// Each export has a tiny worker file that registers its document:
//   // invoicePdf.worker.jsx
//   registerPdfWorker(InvoicePdf);
// and renders through it with:
//   renderPdfInWorker({
//     createWorker: () =>
//       new Worker(new URL("./invoicePdf.worker.jsx", import.meta.url)),
//     Document: InvoicePdf,
//     props: { records, meta },
//     signal,
//   });
// (`new Worker(new URL(...))` must stay in the export's own file so webpack
// can find and bundle the worker.)

export function registerPdfWorker(Document) {
  self.onmessage = async ({ data: props }) => {
    try {
      const blob = await pdf(<Document {...props} />).toBlob();
      self.postMessage({ blob });
    } catch (err) {
      self.postMessage({ error: err?.message || String(err) });
    }
  };
}

function renderOnMainThread(Document, props) {
  return pdf(<Document {...props} />).toBlob();
}

// Resolves with the PDF Blob. Falls back to the main thread if the browser
// can't start the worker. Aborting `signal` terminates the worker.
export function renderPdfInWorker({ createWorker, Document, props, signal }) {
  if (typeof Worker === "undefined") {
    return renderOnMainThread(Document, props);
  }

  return new Promise((resolve, reject) => {
    let worker;
    try {
      worker = createWorker();
    } catch (err) {
      console.warn("PDF worker unavailable, rendering inline", err);
      renderOnMainThread(Document, props).then(resolve, reject);
      return;
    }

    const cleanup = () => {
      worker.terminate();
      signal?.removeEventListener("abort", onAbort);
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException("PDF export cancelled", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort);

    worker.onmessage = ({ data: result }) => {
      cleanup();
      if (result.error) reject(new Error(result.error));
      else resolve(result.blob);
    };
    worker.onerror = (event) => {
      cleanup();
      reject(new Error(event.message || "PDF worker failed"));
    };

    worker.postMessage(props);
  });
}
